import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Grid,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Search as SearchIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { surveyAPI } from '../services/api';
import type { Survey } from '../types';

const SurveyLibrary: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Survey[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const isAdmin = user?.role === 'admin';
  const isManager = isAdmin || user?.role === 'manager';

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await surveyAPI.getSurveys();
        const list: Survey[] = res.data || [];
        const templateList = list.filter((s: any) => !!(s as any).isTemplate);
        setTemplates(templateList);
        setFilteredTemplates(templateList);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to fetch survey templates');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  useEffect(() => {
    // Filter templates based on search query
    if (!searchQuery.trim()) {
      setFilteredTemplates(templates);
    } else {
      const filtered = templates.filter((template: any) =>
        template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (template.description && template.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredTemplates(filtered);
    }
  }, [searchQuery, templates]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const resultLabel = `${filteredTemplates.length} template${filteredTemplates.length !== 1 ? 's' : ''}`;

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/surveys')}
          sx={{ alignSelf: 'flex-start' }}
        >
          Back to Surveys
        </Button>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Survey Library
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Browse our complete collection of survey templates. Choose from professionally designed surveys
              to measure engagement, culture, performance, and more.
            </Typography>
          </Box>

          {/* Search */}
          <TextField
            fullWidth
            placeholder="Search survey templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 360, bgcolor: 'background.paper' }}
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            px: 2,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchQuery ? 'No templates found' : 'No survey templates available'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchQuery ? 'Try adjusting your search terms' : 'Check back later for new templates.'}
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {resultLabel} {searchQuery && `matching "${searchQuery}"`}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {filteredTemplates.map((template: any) => (
              <Grid item xs={12} sm={6} md={4} key={template._id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    boxShadow: 1,
                    transition: 'transform 0.15s ease-out, box-shadow 0.15s ease-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                      <Typography
                        variant="h6"
                        component="h2"
                        sx={{
                          fontSize: '1rem',
                          fontWeight: 600,
                          pr: 1,
                        }}
                        gutterBottom
                      >
                        {template.title}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, minHeight: 48 }}
                    >
                      {template.description || 'No description provided'}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip label="Template" size="small" color="primary" />
                      {template.questions && (
                        <Chip
                          label={`${template.questions.length} questions`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {template.createdAt && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Created: {formatDate(template.createdAt)}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    {isManager ? (
                      <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        onClick={() => navigate(`/surveys/create?templateId=${template._id}`)}
                      >
                        Use This Survey
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        fullWidth
                        onClick={() => navigate(`/surveys/${template._id}/preview`)}
                      >
                        Preview
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Container>
  );
};

export default SurveyLibrary;
